import { cn } from '@seen/ui'
import { IconSparkles } from '@tabler/icons-react'
import { partition } from 'lodash-es'
import {
  type JSX,
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Checkbox } from '#src/components/core/Checkbox.tsx'
import {
  COMBOBOX_CREATE_VALUE,
  COMBOBOX_LIST_MAX_HEIGHT,
  COMBOBOX_NULL_OPTION,
  type ComboboxOption,
  MAX_OPTIONS_TO_SHOW,
  type SuggestedOption,
  useComboboxFiltering,
} from '#src/components/core/combobox/util.ts'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '#src/components/core/Command.tsx'
import { Popover } from '#src/components/core/popover/Popover.tsx'
import { PopoverContent } from '#src/components/core/popover/PopoverContent.tsx'
import { PopoverTrigger } from '#src/components/core/popover/PopoverTrigger.tsx'
import { useDebouncedCallback } from '#src/hooks/useDebouncedCallback.ts'
import { useDidChange } from '#src/hooks/useDidChange.ts'

export type ComboboxMultiOption<TValue extends string | null> =
  ComboboxOption<TValue>
export type SuggestedMultiOption<TValue extends string | null> =
  SuggestedOption<TValue>

export interface ComboboxMultiProps<TValue extends string | null> {
  options: ComboboxMultiOption<TValue>[]
  onSelect: (value: TValue[]) => void
  children: ReactNode
  groupBy?: (option: ComboboxMultiOption<TValue>) => string | undefined
  searchPlaceholder?: string
  values?: TValue[]
  createLabel?: (search: string) => ReactNode
  onCreate?: (search: string) => any
  className?: string
  suggestedOptions?: SuggestedMultiOption<TValue>[]
  suggestedLabel?: string
  disabled?: boolean
  // Optional controlled open state. Pass both to own the popover's
  // open/close — e.g. so selecting OOSA can close the popover before a
  // follow-up modal opens.
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export function ComboboxMulti<TValue extends string | null>({
  onSelect,
  options,
  children,
  groupBy,
  searchPlaceholder = 'Search...',
  values = [],
  createLabel,
  onCreate,
  className,
  suggestedOptions,
  suggestedLabel = 'Suggested',
  disabled = false,
  open: controlledOpen,
  onOpenChange,
}: ComboboxMultiProps<TValue>): JSX.Element {
  const [internalOpen, setInternalOpen] = useState(false)
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : internalOpen
  const setOpen = useCallback(
    (next: boolean): void => {
      if (!isControlled) {
        setInternalOpen(next)
      }
      onOpenChange?.(next)
    },
    [isControlled, onOpenChange]
  )

  const handleSelect = useCallback(
    (value: TValue[], close?: boolean): void => {
      onSelect(value)
      if (close) {
        setOpen(false)
      }
    },
    [onSelect, setOpen]
  )

  const handleCreate = useCallback(
    (search: string): void => {
      onCreate?.(search)
      setOpen(false)
    },
    [onCreate]
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className={cn('p-0', className)} align="start">
        <ComboboxMultiCommand
          onSelect={handleSelect}
          options={options}
          groupBy={groupBy}
          searchPlaceholder={searchPlaceholder}
          selectedValues={values}
          createLabel={createLabel}
          onCreate={handleCreate}
          suggestedOptions={suggestedOptions}
          suggestedLabel={suggestedLabel}
          disabled={disabled}
        />
      </PopoverContent>
    </Popover>
  )
}

interface ComboboxMultiCommandProps<TValue extends string | null> {
  onSelect: (value: TValue[], close?: boolean) => void
  options: ComboboxMultiOption<TValue>[]
  groupBy?: (option: ComboboxMultiOption<TValue>) => string | undefined
  searchPlaceholder?: string
  selectedValues: TValue[]
  createLabel?: (search: string) => ReactNode
  onCreate?: (search: string) => any
  suggestedOptions?: SuggestedMultiOption<TValue>[]
  suggestedLabel?: string
  disabled?: boolean
}

interface ComboboxMultiOptionItemProps<TValue extends string | null> {
  option: ComboboxMultiOption<TValue>
  selectedValues: TValue[]
  onSelect: (value: TValue[], close?: boolean) => void
  tooltip?: ReactNode
  disabled?: boolean
  // cmdk tracks the highlighted item by value, so a second copy of an option
  // needs its own value to stay reachable by keyboard.
  isDuplicate?: boolean
}

const DUPLICATE_VALUE_PREFIX = 'selected-copy:'

function toOptionCommandValue(commandValue: string): string {
  return commandValue.startsWith(DUPLICATE_VALUE_PREFIX)
    ? commandValue.slice(DUPLICATE_VALUE_PREFIX.length)
    : commandValue
}

function ComboboxMultiOptionItem<TValue extends string | null>({
  option,
  selectedValues,
  onSelect,
  tooltip,
  disabled = false,
  isDuplicate = false,
}: ComboboxMultiOptionItemProps<TValue>): JSX.Element {
  const isSelected = selectedValues.includes(option.value)
  const commandValue = option.value ?? COMBOBOX_NULL_OPTION

  return (
    <CommandItem
      key={option.value}
      value={
        isDuplicate ? `${DUPLICATE_VALUE_PREFIX}${commandValue}` : commandValue
      }
      onSelect={(selectedCommandValue) => {
        if (disabled) {
          return
        }
        const value = toOptionCommandValue(selectedCommandValue)
        if (value === COMBOBOX_NULL_OPTION) {
          onSelect([], true)
        } else {
          const next = selectedValues.filter((v) => v !== value)
          onSelect(isSelected ? next : [...next, value as TValue], true)
        }
      }}
      className={cn(
        'group items-center gap-2 p-0',
        disabled ? 'cursor-default opacity-60' : 'cursor-pointer'
      )}
    >
      <div className="flex w-full items-center">
        <div
          className={cn(
            'flex items-center p-2 pr-4',
            disabled && 'pointer-events-none'
          )}
          onClick={(e) => {
            e.stopPropagation()
            if (disabled) {
              return
            }
            const next = selectedValues.filter((v) => v !== option.value)
            onSelect(isSelected ? next : [...next, option.value], false)
          }}
        >
          <Checkbox
            className={cn(
              'text-muted-foreground',
              disabled
                ? 'opacity-50'
                : 'hover:opacity-100! group-hover:visible',
              !isSelected && !disabled && 'invisible group-hover:opacity-50'
            )}
            value={isSelected}
          />
        </div>
        <div className="flex-1 p-2 pl-0">{option.label}</div>
        {tooltip}
      </div>
    </CommandItem>
  )
}

export function ComboboxMultiCommand<TValue extends string | null>({
  onSelect,
  options,
  groupBy,
  searchPlaceholder = 'Search...',
  selectedValues,
  createLabel,
  onCreate,
  suggestedOptions = [],
  suggestedLabel = 'Suggested',
  disabled = false,
}: ComboboxMultiCommandProps<TValue>): JSX.Element {
  const [isNavigationMode, setIsNavigationMode] = useState(false)
  const [changedValuesInSession, setChangedValuesInSession] = useState<
    TValue[]
  >([])
  const inputRef = useRef<HTMLInputElement>(null)
  const commandRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setChangedValuesInSession([])
    setIsNavigationMode(false)
  }, [])

  const [search, setSearch] = useState('')

  const {
    regularOptions,
    allIndexedOptions,
    filteredSuggestedOptions,
    filteredOptions,
  } = useComboboxFiltering({
    options,
    suggestedOptions,
    search,
    groupBy,
  })

  const firstValue =
    filteredSuggestedOptions[0]?.value ??
    filteredOptions[0]?.value ??
    allIndexedOptions[0]?.value ??
    ''
  const [highlightedValue, setHighlightedValue] = useState(firstValue)
  const didFirstValueChange = useDidChange(firstValue)
  if (didFirstValueChange) {
    setHighlightedValue(firstValue)
  }

  const debouncedSearch = useDebouncedCallback((value: string) => {
    setSearch(value)
    if (value !== search) {
      setIsNavigationMode(false)
    }
  }, 50)

  const selectedValueSet = useMemo(
    () => new Set(selectedValues),
    [selectedValues]
  )

  const [selectedOptions, nonSelectedOptions] = useMemo(
    () => partition(regularOptions, (o) => selectedValueSet.has(o.value)),
    [regularOptions, selectedValueSet]
  )

  const [selectedSuggestedOptions, nonSelectedSuggestedOptions] = useMemo(
    () => partition(suggestedOptions, (o) => selectedValueSet.has(o.value)),
    [suggestedOptions, selectedValueSet]
  )

  const allFilteredOptions = useMemo(
    () => [...filteredSuggestedOptions, ...filteredOptions],
    [filteredSuggestedOptions, filteredOptions]
  )

  const renderOptionGroups = (
    optionsToRender: ComboboxMultiOption<TValue>[]
  ): ReactNode => {
    const groups = new Map<string | undefined, ComboboxMultiOption<TValue>[]>()
    for (const option of optionsToRender) {
      const group = groupBy?.(option)
      const groupedOptions = groups.get(group)
      if (groupedOptions) {
        groupedOptions.push(option)
      } else {
        groups.set(group, [option])
      }
    }

    return [...groups.entries()].map(([group, groupedOptions]) => (
      <CommandGroup key={group ?? 'ungrouped'} heading={group}>
        {groupedOptions.map((option) => (
          <ComboboxMultiOptionItem
            key={option.value}
            option={option}
            selectedValues={selectedValues}
            onSelect={onSelect}
            disabled={disabled}
          />
        ))}
      </CommandGroup>
    ))
  }

  const highlightedOptionValue = toOptionCommandValue(highlightedValue)

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        setIsNavigationMode(true)
      }

      if (e.key === ' ' && isNavigationMode) {
        e.preventDefault()

        if (disabled) {
          return
        }

        if (highlightedValue === COMBOBOX_CREATE_VALUE) {
          onCreate?.(search)
          return
        }

        const option = allFilteredOptions.find(
          (opt) => opt.value === highlightedOptionValue
        )
        if (option) {
          const isCurrentlySelected = selectedValues.includes(option.value)
          const next = selectedValues.filter((v) => v !== option.value)

          if (isCurrentlySelected) {
            onSelect(next)
            setChangedValuesInSession((prev) =>
              prev.includes(option.value) ? prev : [...prev, option.value]
            )
          } else {
            onSelect([...next, option.value])
            setChangedValuesInSession((prev) =>
              prev.includes(option.value) ? prev : [...prev, option.value]
            )
          }
        }
      }

      if (e.key === 'Enter') {
        if (disabled) {
          e.preventDefault()
          onSelect(selectedValues, true)
          return
        }

        const option = allFilteredOptions.find(
          (opt) => opt.value === highlightedOptionValue
        )

        const wasChangedInSession =
          option && changedValuesInSession.includes(option.value)

        if (isNavigationMode) {
          if (wasChangedInSession) {
            e.preventDefault() // prevent [de]selection of the highlighted option
          }
          onSelect(selectedValues, true)
          return
        }

        if (option) {
          const isCurrentlySelected = selectedValues.includes(option.value)
          const next = selectedValues.filter((v) => v !== option.value)

          if (isCurrentlySelected) {
            onSelect(next, true)
          } else {
            onSelect([...next, option.value], true)
          }
        } else {
          onSelect(selectedValues, true)
        }
      }

      if (e.key === 'Escape') {
        e.preventDefault()
        onSelect(selectedValues, true)
      }

      if (e.key.length === 1 && e.key !== ' ') {
        setIsNavigationMode(false)
      }
    },
    [
      isNavigationMode,
      allFilteredOptions,
      highlightedValue,
      highlightedOptionValue,
      selectedValues,
      onSelect,
      changedValuesInSession,
      onCreate,
      search,
      disabled,
    ]
  )

  useEffect(() => {
    setIsNavigationMode(false)
  }, [])

  return (
    <Command
      shouldFilter={false}
      value={highlightedValue} // value needs to be controlled for highlighting to work properly if shouldFilter is false (i.e. we highlight the first option above when the search text changes)
      onValueChange={setHighlightedValue}
      onKeyDown={handleKeyDown}
      ref={commandRef}
    >
      <CommandInput
        // We don't use the value prop because when it's used, the list scroll position doesn't reset properly when you press backspace in the input.
        onValueChange={debouncedSearch}
        placeholder={searchPlaceholder}
        className="rounded-none border-b px-3"
        autoFocus
        ref={inputRef}
      />
      <CommandList
        key={`${search}-${filteredOptions.length}`} // We use a key to reset the scroll position when the search text changes.
        className={COMBOBOX_LIST_MAX_HEIGHT}
      >
        <CommandEmpty>None found.</CommandEmpty>
        {!search && nonSelectedSuggestedOptions.length > 0 && (
          <>
            <CommandGroup
              heading={
                <span className="flex items-center gap-1.5">
                  <IconSparkles className="size-3.5 shrink-0 text-green-600" />
                  {suggestedLabel}
                </span>
              }
            >
              {nonSelectedSuggestedOptions.map((option) => (
                <ComboboxMultiOptionItem
                  key={`suggested-${option.value}`}
                  option={option}
                  selectedValues={selectedValues}
                  onSelect={onSelect}
                  tooltip={option.tooltip}
                  disabled={disabled}
                />
              ))}
            </CommandGroup>
            {(selectedOptions.length > 0 ||
              nonSelectedOptions.length > 0 ||
              selectedSuggestedOptions.length > 0) && <CommandSeparator />}
          </>
        )}
        {search && filteredSuggestedOptions.length > 0 && (
          <>
            <CommandGroup>
              {filteredSuggestedOptions.map((option) => (
                <ComboboxMultiOptionItem
                  key={`suggested-${option.value}`}
                  option={option}
                  selectedValues={selectedValues}
                  onSelect={onSelect}
                  tooltip={option.tooltip}
                  disabled={disabled}
                />
              ))}
            </CommandGroup>
            {filteredOptions.length > 0 && <CommandSeparator />}
          </>
        )}
        {groupBy ? (
          <>
            {!search &&
              (selectedSuggestedOptions.length > 0 ||
                selectedOptions.length > 0) && (
                <>
                  <CommandGroup heading="Selected">
                    {selectedSuggestedOptions.map((option) => (
                      <ComboboxMultiOptionItem
                        key={`selected-suggested-${option.value}`}
                        option={option}
                        selectedValues={selectedValues}
                        onSelect={onSelect}
                        tooltip={option.tooltip}
                        disabled={disabled}
                      />
                    ))}
                    {selectedOptions.map((option) => (
                      <ComboboxMultiOptionItem
                        key={`selected-${option.value}`}
                        option={option}
                        selectedValues={selectedValues}
                        onSelect={onSelect}
                        disabled={disabled}
                        isDuplicate
                      />
                    ))}
                  </CommandGroup>
                  {regularOptions.length > 0 && <CommandSeparator />}
                </>
              )}
            {!search &&
              renderOptionGroups(regularOptions.slice(0, MAX_OPTIONS_TO_SHOW))}
            {search &&
              renderOptionGroups(filteredOptions.slice(0, MAX_OPTIONS_TO_SHOW))}
          </>
        ) : (
          <CommandGroup>
            {!search && (
              <>
                {selectedSuggestedOptions.map((option) => (
                  <ComboboxMultiOptionItem
                    key={`selected-suggested-${option.value}`}
                    option={option}
                    selectedValues={selectedValues}
                    onSelect={onSelect}
                    tooltip={option.tooltip}
                    disabled={disabled}
                  />
                ))}
                {selectedOptions.map((option) => (
                  <ComboboxMultiOptionItem
                    key={option.value}
                    option={option}
                    selectedValues={selectedValues}
                    onSelect={onSelect}
                    disabled={disabled}
                  />
                ))}
                {(selectedOptions.length > 0 ||
                  selectedSuggestedOptions.length > 0) &&
                  nonSelectedOptions.length > 0 && <CommandSeparator />}
                {nonSelectedOptions
                  .slice(0, MAX_OPTIONS_TO_SHOW)
                  .map((option) => (
                    <ComboboxMultiOptionItem
                      key={option.value}
                      option={option}
                      selectedValues={selectedValues}
                      onSelect={onSelect}
                      disabled={disabled}
                    />
                  ))}
              </>
            )}
            {search &&
              filteredOptions
                .slice(0, MAX_OPTIONS_TO_SHOW)
                .map((option) => (
                  <ComboboxMultiOptionItem
                    key={option.value}
                    option={option}
                    selectedValues={selectedValues}
                    onSelect={onSelect}
                    disabled={disabled}
                  />
                ))}
          </CommandGroup>
        )}
        <CommandGroup>
          {search.length > 1 && createLabel && !disabled && (
            <CommandItem
              value={COMBOBOX_CREATE_VALUE}
              onSelect={() => onCreate?.(search)}
            >
              {createLabel(search)}
            </CommandItem>
          )}
          {filteredOptions.length > MAX_OPTIONS_TO_SHOW && (
            <CommandItem disabled>Search to see more results…</CommandItem>
          )}
        </CommandGroup>
      </CommandList>
    </Command>
  )
}
